import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, InputBase, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import SearchIcon from '@mui/icons-material/Search';
import SendIcon from '@mui/icons-material/Send';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { resolveResolvedTheme, WidgetBuilderConfig } from '../../types/widgetBuilder';

type Props = {
  config: WidgetBuilderConfig;
  device: 'desktop' | 'mobile';
};

/** Screens that can be shown inside the bot preview. More views can be added later. */
export type ChatBotPreviewView = 'welcome' | 'conversation' | 'history' | 'browse' | 'integration';

export type ConversationMessage = {
  id: string;
  role: 'user' | 'bot';
  text: string;
  createdAt: number;
};

const createMessage = (role: ConversationMessage['role'], text: string): ConversationMessage => ({
  id: `${role}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  role,
  text,
  createdAt: Date.now(),
});

const BOT_GREETING = 'Hi! I am your AI assistant. How can I help you today?';

const SUPPORT_BOT_REPLY =
  'I can help you browse the available questions or connect you with support. This is a local preview response.';

const STARTER_QUESTIONS = [
  'How do I get started?',
  'Where can I see my previous requests?',
  'Talk to a person',
];

const BROWSE_QUESTIONS = [
  'How do I get started?',
  'How can I update my account details?',
  'Where can I see my previous requests?',
  'How do I change notifications?',
  'How can I contact support?',
  'How do I manage my billing plan?',
];

const HISTORY_SESSIONS = [
  {
    id: 'h1',
    title: 'How do I get started?',
    snippet: 'Welcome! Here is a quick overview of…',
    meta: '8 Oct',
  },
  {
    id: 'h2',
    title: 'I need help with an issue',
    snippet: 'Thanks for sharing the details. I can…',
    meta: 'Example',
  },
  {
    id: 'h3',
    title: 'Account settings',
    snippet: 'You can update your profile from…',
    meta: 'Example',
  },
  {
    id: 'h4',
    title: 'Previous requests',
    snippet: 'Here are the recent tickets linked…',
    meta: 'Example',
  },
];

type ThemeTokens = {
  isDark: boolean;
  brand: string;
  /** Primary accent (brand) — used for option icons 01/02 and hover */
  primaryAccent: string;
  /** Secondary accent — used for option icon 03 */
  secondaryAccent: string;
  primary: string;
  secondary: string;
  surface: string;
  panel: string;
  border: string;
  heroBg: string;
  muted: string;
  fontSize: number;
  fontFamily: string;
};

const withAlpha = (hex: string, alphaHex: string) => {
  const normalized = hex.replace('#', '');
  if (normalized.length !== 6) return `${hex}${alphaHex}`;
  return `#${normalized}${alphaHex}`;
};

const hideScrollbarSx = {
  scrollbarWidth: 'none',
  msOverflowStyle: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
    width: 0,
    height: 0,
  },
} as const;

const buildTokens = (config: WidgetBuilderConfig): ThemeTokens => {
  const mode = resolveResolvedTheme(config.theme);
  const isDark = mode === 'dark';
  const brand = config.colors.brand_color || '#007AFF';
  // Brand / Primary color → primary accent; Secondary color → secondary accent (with warm fallback).
  const primaryAccent = brand;
  const configuredSecondary = isDark
    ? config.colors.secondary_text_dark
    : config.colors.secondary_text_light;
  const secondaryAccent =
    configuredSecondary &&
    !['#6b7280', '#989898', '#64748B', '#94A3B8'].includes(configuredSecondary)
      ? configuredSecondary
      : isDark
        ? '#FB7185'
        : '#F43F5E';

  return {
    isDark,
    brand,
    primaryAccent,
    secondaryAccent,
    primary: isDark ? config.colors.primary_text_dark : config.colors.primary_text_light,
    secondary: isDark ? config.colors.secondary_text_dark : config.colors.secondary_text_light,
    surface: isDark ? '#0B1220' : '#FFFFFF',
    panel: isDark ? '#111827' : '#F8FAFC',
    border: isDark ? 'rgba(148,163,184,0.18)' : 'rgba(15, 23, 42, 0.08)',
    heroBg: isDark ? '#0B1F3A' : '#EEF5FF',
    muted: isDark ? 'rgba(148,163,184,0.55)' : '#94A3B8',
    fontSize:
      config.typography.size === 'compact' ? 12 : config.typography.size === 'comfortable' ? 14.5 : 13,
    fontFamily: `"${config.typography.font}", sans-serif`,
  };
};

const ChatBotPreview: React.FC<Props> = ({ config, device }) => {
  const tokens = useMemo(() => buildTokens(config), [config]);
  const [view, setView] = useState<ChatBotPreviewView>('welcome');
  const [conversation, setConversation] = useState<ConversationMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [showStarterQuestions, setShowStarterQuestions] = useState(false);

  // Reset to welcome when theme/variant identity changes so first paint is always welcome.
  useEffect(() => {
    setView('welcome');
    setConversation([]);
    setDraft('');
    setShowStarterQuestions(false);
  }, [config.varient_id, config.theme]);

  const goWelcome = () => {
    setShowStarterQuestions(false);
    setView('welcome');
  };

  const startConversation = (seedUserText?: string, options?: { withStarters?: boolean }) => {
    const withStarters = Boolean(options?.withStarters);
    if (seedUserText?.trim() && withStarters) {
      setConversation([
        createMessage('user', seedUserText.trim()),
        createMessage('bot', SUPPORT_BOT_REPLY),
      ]);
      setShowStarterQuestions(true);
    } else {
      setConversation((prev) => {
        const next = prev.length > 0 ? [...prev] : [createMessage('bot', BOT_GREETING)];
        if (seedUserText?.trim()) {
          next.push(createMessage('user', seedUserText.trim()));
        }
        return next;
      });
      setShowStarterQuestions(false);
    }
    setView('conversation');
  };

  const handleSend = () => {
    const text = draft.trim();
    if (!text) return;

    if (view !== 'conversation') {
      startConversation(text);
    } else {
      setConversation((prev) => {
        const base = prev.length > 0 ? prev : [createMessage('bot', BOT_GREETING)];
        return [...base, createMessage('user', text)];
      });
      setShowStarterQuestions(false);
    }
    setDraft('');
  };

  const handleStarterSelect = (question: string) => {
    setConversation((prev) => [...prev, createMessage('user', question)]);
    setShowStarterQuestions(false);
  };

  const handleNewSession = () => {
    if (!config.bot_features.is_add_new_session) return;
    setConversation([createMessage('bot', BOT_GREETING)]);
    setDraft('');
    setShowStarterQuestions(false);
    setView('conversation');
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: device === 'mobile' ? 360 : 420,
        height: device === 'mobile' ? 'min(560px, 100%)' : 'min(720px, calc(100vh - 220px))',
        maxHeight: '100%',
        mx: 'auto',
        borderRadius: 3.5,
        overflow: 'hidden',
        border: `1px solid ${tokens.border}`,
        backgroundColor: tokens.surface,
        color: tokens.primary,
        fontFamily: tokens.fontFamily,
        fontSize: tokens.fontSize,
        boxShadow: '0 28px 60px rgba(15, 23, 42, 0.16)',
        display: 'flex',
        flexDirection: 'column',
        textAlign: 'left',
        alignItems: 'stretch',
        ...hideScrollbarSx,
        '& *': hideScrollbarSx,
      }}
    >
      <BotHeader
        tokens={tokens}
        config={config}
        onHistory={() => config.bot_features.is_history && setView('history')}
        onNewSession={handleNewSession}
        onClose={goWelcome}
      />

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', position: 'relative' }}>
        {(view === 'welcome' || view === 'browse') && (
          <WelcomeView
            tokens={tokens}
            config={config}
            onAction={(next, seed) => {
              if (next === 'conversation') {
                startConversation(seed, { withStarters: Boolean(seed) });
                return;
              }
              setView(next);
            }}
            draft={draft}
            onDraftChange={setDraft}
            onSend={handleSend}
          />
        )}
        {view === 'conversation' && (
          <ConversationView
            tokens={tokens}
            config={config}
            conversation={conversation}
            showStarterQuestions={showStarterQuestions}
            onStarterSelect={handleStarterSelect}
            draft={draft}
            onDraftChange={setDraft}
            onSend={handleSend}
          />
        )}
        {view === 'history' && (
          <HistoryView
            tokens={tokens}
            onOpenSession={(title) => startConversation(title)}
            onNewConversation={handleNewSession}
          />
        )}
        {view === 'integration' && (
          <PlaceholderView
            tokens={tokens}
            title="Plan an integration"
            subtitle="API and SDK guidance will appear here."
            onBack={goWelcome}
          />
        )}

        {view === 'browse' && (
          <BrowseBottomSheet
            tokens={tokens}
            draft={draft}
            onDraftChange={setDraft}
            onSend={handleSend}
            onClose={goWelcome}
            onSelectQuestion={(question) => startConversation(question)}
          />
        )}
      </Box>
    </Box>
  );
};

const BotHeader: React.FC<{
  tokens: ThemeTokens;
  config: WidgetBuilderConfig;
  onHistory: () => void;
  onNewSession: () => void;
  onClose: () => void;
}> = ({ tokens, config, onHistory, onNewSession, onClose }) => (
  <Box
    sx={{
      px: 1.75,
      py: 1.35,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 1,
      flexShrink: 0,
      borderBottom: `1px solid ${tokens.border}`,
      backgroundColor: tokens.surface,
    }}
  >
    <Stack direction="row" spacing={1.15} alignItems="center" sx={{ minWidth: 0 }}>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: 2,
          backgroundColor: tokens.isDark ? 'rgba(56,189,248,0.12)' : '#E0F2FE',
          color: tokens.brand,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <SmartToyOutlinedIcon sx={{ fontSize: 20 }} />
      </Box>
      <Box sx={{ minWidth: 0, textAlign: 'left' }}>
        <Typography
          noWrap
          sx={{ fontWeight: 700, fontSize: tokens.fontSize + 1, color: tokens.primary, textAlign: 'left' }}
        >
          Support chatbot
        </Typography>
        <Stack direction="row" spacing={0.6} alignItems="center" justifyContent="flex-start">
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#22C55E' }} />
          <Typography sx={{ fontSize: tokens.fontSize - 1.5, color: tokens.secondary, textAlign: 'left' }}>
            Ready to help
          </Typography>
        </Stack>
      </Box>
    </Stack>

    <Stack direction="row" spacing={0.35} alignItems="center">
      {config.bot_features.is_history && (
        <IconAction tokens={tokens} onClick={onHistory} title="History">
          <HistoryIcon sx={{ fontSize: 18 }} />
        </IconAction>
      )}
      {config.bot_features.is_add_new_session && (
        <IconAction tokens={tokens} onClick={onNewSession} title="New session">
          <AddIcon sx={{ fontSize: 18 }} />
        </IconAction>
      )}
      {config.bot_features.is_request_agent && (
        <IconAction tokens={tokens} onClick={onNewSession} title="Request agent">
          <SupportAgentIcon sx={{ fontSize: 17 }} />
        </IconAction>
      )}
      <IconAction tokens={tokens} onClick={onClose} title="Close" filled>
        <CloseIcon sx={{ fontSize: 16 }} />
      </IconAction>
    </Stack>
  </Box>
);

const IconAction: React.FC<{
  tokens: ThemeTokens;
  onClick: () => void;
  title: string;
  filled?: boolean;
  children: React.ReactNode;
}> = ({ tokens, onClick, title, filled, children }) => (
  <Box
    component="button"
    title={title}
    onClick={onClick}
    sx={{
      width: 30,
      height: 30,
      border: 0,
      borderRadius: 1.5,
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: tokens.secondary,
      backgroundColor: filled
        ? tokens.isDark
          ? 'rgba(148,163,184,0.14)'
          : '#F1F5F9'
        : 'transparent',
      '&:hover': {
        backgroundColor: tokens.isDark ? 'rgba(148,163,184,0.18)' : '#F1F5F9',
        color: tokens.primary,
      },
    }}
  >
    {children}
  </Box>
);

const WelcomeView: React.FC<{
  tokens: ThemeTokens;
  config: WidgetBuilderConfig;
  onAction: (view: ChatBotPreviewView, seed?: string) => void;
  draft: string;
  onDraftChange: (value: string) => void;
  onSend: () => void;
}> = ({ tokens, config, onAction, draft, onDraftChange, onSend }) => (
  <>
    <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', ...hideScrollbarSx }}>
      <Box
        sx={{
          position: 'relative',
          px: 2.25,
          pt: 2.25,
          pb: 2.5,
          minHeight: 168,
          backgroundColor: tokens.heroBg,
          overflow: 'hidden',
          textAlign: 'left',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
        }}
      >
        {/* Soft circular arc behind content */}
        <Box
          sx={{
            position: 'absolute',
            right: -70,
            top: -36,
            width: 220,
            height: 220,
            borderRadius: '50%',
            border: `2px solid ${tokens.isDark ? 'rgba(96,165,250,0.22)' : 'rgba(96,165,250,0.35)'}`,
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            right: -40,
            top: 8,
            width: 170,
            height: 170,
            borderRadius: '50%',
            border: `1.5px solid ${tokens.isDark ? 'rgba(96,165,250,0.12)' : 'rgba(147,197,253,0.45)'}`,
            pointerEvents: 'none',
          }}
        />

        {/* Pink accent blotch */}
        <Box
          sx={{
            position: 'absolute',
            right: 88,
            top: 28,
            width: 18,
            height: 18,
            borderRadius: '50%',
            background: 'radial-gradient(circle at 40% 35%, #FDA4AF 0%, #FB7185 55%, #F43F5E 100%)',
            opacity: 0.9,
            filter: 'blur(0.2px)',
            zIndex: 1,
          }}
        />

        {/* Organic blue blob + chat icon */}
        <Box
          sx={{
            position: 'absolute',
            right: -22,
            bottom: -28,
            width: 128,
            height: 128,
            borderRadius: '62% 38% 48% 52% / 48% 55% 45% 52%',
            background: `linear-gradient(155deg, ${tokens.brand} 0%, #3B82F6 55%, ${tokens.brand}CC 100%)`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: `0 14px 28px ${tokens.brand}40`,
            zIndex: 1,
          }}
        >
          <Box
            component="svg"
            viewBox="0 0 48 44"
            sx={{ width: 44, height: 40, display: 'block', ml: -0.5, mt: -0.5 }}
          >
            <path
              d="M8 6h28a6 6 0 0 1 6 6v14a6 6 0 0 1-6 6H20l-9 8v-8H8a6 6 0 0 1-6-6V12a6 6 0 0 1 6-6z"
              fill="none"
              stroke="#FFFFFF"
              strokeWidth="3.2"
              strokeLinejoin="round"
            />
          </Box>
        </Box>

        <Box
          sx={{
            position: 'relative',
            zIndex: 2,
            maxWidth: '68%',
            width: '100%',
            textAlign: 'left',
            alignSelf: 'flex-start',
          }}
        >
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              alignSelf: 'flex-start',
              gap: 0.75,
              px: 1.2,
              py: 0.55,
              mb: 1.6,
              borderRadius: 999,
              backgroundColor: tokens.isDark ? 'rgba(15,23,42,0.65)' : '#FFFFFF',
              boxShadow: tokens.isDark
                ? '0 4px 12px rgba(0,0,0,0.25)'
                : '0 4px 14px rgba(15, 23, 42, 0.08)',
            }}
          >
            <Box
              sx={{
                width: 7,
                height: 7,
                borderRadius: '50%',
                backgroundColor: tokens.isDark ? '#38BDF8' : '#60A5FA',
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '0.06em',
                color: tokens.brand,
                textTransform: 'uppercase',
                lineHeight: 1,
                textAlign: 'left',
              }}
            >
              Help is here
            </Typography>
          </Box>

          <Typography
            sx={{
              fontWeight: 800,
              fontSize: tokens.fontSize + 8,
              lineHeight: 1.15,
              letterSpacing: '-0.025em',
              color: tokens.primary,
              mb: 1,
              textAlign: 'left',
            }}
          >
            How can we
            <Box component="span" sx={{ display: 'block', color: tokens.brand, textAlign: 'left' }}>
              connect today?
            </Box>
          </Typography>
          <Typography
            sx={{
              fontSize: tokens.fontSize - 0.5,
              fontWeight: 400,
              color: tokens.isDark ? tokens.secondary : '#6B7280',
              lineHeight: 1.45,
              textAlign: 'left',
            }}
          >
            Answers, setup guidance and real support in one place.
          </Typography>
        </Box>
      </Box>

      <Box sx={{ px: 2, pt: 1.75, pb: 1.5, textAlign: 'left', width: '100%' }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ mb: 1.25, width: '100%' }}
        >
          <Typography
            sx={{
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: tokens.muted,
              textAlign: 'left',
            }}
          >
            Your next step
          </Typography>
          <Typography sx={{ fontSize: 11, color: tokens.brand, fontWeight: 500, textAlign: 'right' }}>
            Choose your way in
          </Typography>
        </Stack>

        <Box
          onClick={() => onAction('conversation')}
          sx={{
            mb: 1.25,
            p: 1.35,
            borderRadius: 2.5,
            border: `1px solid ${tokens.border}`,
            backgroundColor: tokens.isDark ? '#111827' : '#FFFFFF',
            boxShadow: tokens.isDark ? 'none' : '0 8px 20px rgba(15,23,42,0.06)',
            display: 'flex',
            alignItems: 'center',
            gap: 1.15,
            cursor: 'pointer',
            '&:hover': { borderColor: `${tokens.primaryAccent}66` },
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              backgroundColor: withAlpha(tokens.primaryAccent, tokens.isDark ? '28' : '18'),
              color: tokens.primaryAccent,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <ChatBubbleOutlineIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
            <Typography
              sx={{ fontWeight: 700, fontSize: tokens.fontSize, color: tokens.primary, textAlign: 'left' }}
            >
              Start a conversation
            </Typography>
            <Typography
              sx={{ fontSize: tokens.fontSize - 1.5, color: tokens.secondary, textAlign: 'left' }}
            >
              Tell us what you need
            </Typography>
          </Box>
          <ArrowOutwardIcon sx={{ fontSize: 18, color: tokens.primaryAccent }} />
        </Box>

        <Box sx={{ mx: -2 }}>
          <ActionRow
            tokens={tokens}
            index="01"
            accent="primary"
            icon={<SearchIcon sx={{ fontSize: 18 }} />}
            title="Browse questions"
            subtitle="Find an answer in the FAQ"
            onClick={() => onAction('browse')}
          />
          <ActionRow
            tokens={tokens}
            index="02"
            accent="primary"
            icon={<ChatBubbleOutlineIcon sx={{ fontSize: 18 }} />}
            title="Get help with an issue"
            subtitle="Tell us what happened"
            onClick={() => onAction('conversation', 'I need help with an issue')}
            last={!config.bot_features.is_request_agent}
          />
          {config.bot_features.is_request_agent && (
            <ActionRow
              tokens={tokens}
              index="03"
              accent="primary"
              icon={<PeopleAltOutlinedIcon sx={{ fontSize: 18 }} />}
              title="Talk to support"
              subtitle="Get human help"
              onClick={() => onAction('conversation', 'Talk to a person')}
              last
            />
          )}
        </Box>
      </Box>
    </Box>

    <ComposerFooter
      tokens={tokens}
      placeholder="Ask a support question..."
      value={draft}
      onChange={onDraftChange}
      onSend={onSend}
    />
  </>
);

const ActionRow: React.FC<{
  tokens: ThemeTokens;
  index: string;
  accent: 'primary' | 'secondary';
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  onClick: () => void;
  last?: boolean;
}> = ({ tokens, index, accent, icon, title, subtitle, onClick, last }) => {
  const accentColor = accent === 'primary' ? tokens.primaryAccent : tokens.secondaryAccent;
  const hoverBg = withAlpha(tokens.primaryAccent, tokens.isDark ? '24' : '14');
  const hoverBorder = withAlpha(tokens.primaryAccent, tokens.isDark ? '55' : '40');

  return (
    <Box
      onClick={onClick}
      sx={{
        px: 2,
        py: 1.35,
        display: 'flex',
        alignItems: 'center',
        gap: 1.15,
        cursor: 'pointer',
        borderTop: '1px solid transparent',
        borderBottom: last ? '1px solid transparent' : `1px solid ${tokens.border}`,
        transition: 'background-color 0.15s ease, border-color 0.15s ease',
        '&:hover': {
          backgroundColor: hoverBg,
          borderTopColor: hoverBorder,
          borderBottomColor: hoverBorder,
        },
      }}
    >
      <Typography
        sx={{
          width: 22,
          fontSize: 11,
          fontWeight: 600,
          color: tokens.muted,
          textAlign: 'left',
          flexShrink: 0,
        }}
      >
        {index}
      </Typography>
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: 1.75,
          backgroundColor: withAlpha(accentColor, tokens.isDark ? '28' : '18'),
          color: accentColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: tokens.fontSize - 0.5,
            color: tokens.primary,
            textAlign: 'left',
            lineHeight: 1.3,
          }}
        >
          {title}
        </Typography>
        <Typography
          sx={{
            fontSize: tokens.fontSize - 2,
            color: tokens.secondary,
            textAlign: 'left',
            lineHeight: 1.35,
          }}
        >
          {subtitle}
        </Typography>
      </Box>
      <ArrowOutwardIcon sx={{ fontSize: 16, color: tokens.muted, flexShrink: 0 }} />
    </Box>
  );
};

const ConversationView: React.FC<{
  tokens: ThemeTokens;
  config: WidgetBuilderConfig;
  conversation: ConversationMessage[];
  showStarterQuestions: boolean;
  onStarterSelect: (question: string) => void;
  draft: string;
  onDraftChange: (value: string) => void;
  onSend: () => void;
}> = ({
  tokens,
  config,
  conversation,
  showStarterQuestions,
  onStarterSelect,
  draft,
  onDraftChange,
  onSend,
}) => {
  const listRef = useRef<HTMLDivElement>(null);
  const [hoveredStarter, setHoveredStarter] = useState<string | null>(null);

  useEffect(() => {
    const el = listRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [conversation, showStarterQuestions]);

  return (
    <>
      <Box
        ref={listRef}
        sx={{
          flex: 1,
          px: 2,
          py: 1.75,
          overflow: 'auto',
          backgroundColor: tokens.isDark ? '#000000' : '#FFFFFF',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          ...hideScrollbarSx,
        }}
      >
        <Stack spacing={1.35} sx={{ mt: 'auto' }}>
          {conversation.map((message) => (
            <Bubble
              key={message.id}
              tokens={tokens}
              side={message.role === 'user' ? 'right' : 'left'}
              brand={message.role === 'user' ? tokens.brand : undefined}
            >
              {message.text}
            </Bubble>
          ))}

          {showStarterQuestions && (
            <Box sx={{ pt: 0.75, textAlign: 'left' }}>
              <Typography
                sx={{
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: tokens.muted,
                  mb: 1,
                }}
              >
                Start with a question
              </Typography>
              <Stack spacing={0.85}>
                {STARTER_QUESTIONS.map((question) => {
                  const isActive = hoveredStarter === question;
                  return (
                    <Box
                      key={question}
                      onClick={() => onStarterSelect(question)}
                      onMouseEnter={() => setHoveredStarter(question)}
                      onMouseLeave={() => setHoveredStarter(null)}
                      sx={{
                        px: 1.35,
                        py: 1.05,
                        borderRadius: 2.25,
                        border: `1px solid ${
                          isActive ? withAlpha(tokens.primaryAccent, '55') : tokens.border
                        }`,
                        backgroundColor: isActive
                          ? withAlpha(tokens.primaryAccent, tokens.isDark ? '28' : '14')
                          : tokens.isDark
                            ? '#111827'
                            : '#FFFFFF',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        cursor: 'pointer',
                        transition: 'background-color 0.15s ease, border-color 0.15s ease',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: tokens.fontSize - 0.5,
                          fontWeight: 600,
                          color: tokens.primaryAccent,
                          textAlign: 'left',
                          lineHeight: 1.35,
                        }}
                      >
                        {question}
                      </Typography>
                      <ArrowForwardIosIcon
                        sx={{ fontSize: 12, color: tokens.primaryAccent, flexShrink: 0 }}
                      />
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          )}

          {!showStarterQuestions &&
            config.bot_features.is_request_agent &&
            conversation.length > 0 && (
              <Typography
                sx={{ fontSize: tokens.fontSize - 2, color: tokens.secondary, textAlign: 'center' }}
              >
                Need a person? Use Request agent from the header.
              </Typography>
            )}
        </Stack>
      </Box>
      <ComposerFooter
        tokens={tokens}
        placeholder="Ask anything…"
        value={draft}
        onChange={onDraftChange}
        onSend={onSend}
      />
    </>
  );
};

const Bubble: React.FC<{
  tokens: ThemeTokens;
  side: 'left' | 'right';
  brand?: string;
  children: React.ReactNode;
}> = ({ tokens, side, brand, children }) => {
  const botBubbleBg = tokens.isDark ? '#223346' : '#f0f5fb';

  return (
    <Box sx={{ alignSelf: side === 'left' ? 'flex-start' : 'flex-end', maxWidth: '88%' }}>
      <Box
        sx={{
          px: 1.4,
          py: 1.05,
          borderRadius: side === 'left' ? '14px 14px 14px 4px' : '14px 14px 4px 14px',
          backgroundColor: side === 'left' ? botBubbleBg : brand,
          color: side === 'left' ? tokens.primary : '#fff',
          border: 'none',
          boxShadow: side === 'left' ? 'none' : `0 8px 16px ${brand}40`,
        }}
      >
        {children}
      </Box>
    </Box>
  );
};

const BrowseBottomSheet: React.FC<{
  tokens: ThemeTokens;
  draft: string;
  onDraftChange: (value: string) => void;
  onSend: () => void;
  onClose: () => void;
  onSelectQuestion: (question: string) => void;
}> = ({ tokens, draft, onDraftChange, onSend, onClose, onSelectQuestion }) => {
  const [query, setQuery] = useState('');
  const [hovered, setHovered] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return BROWSE_QUESTIONS;
    return BROWSE_QUESTIONS.filter((item) => item.toLowerCase().includes(q));
  }, [query]);

  return (
    <Box
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: 20,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-end',
        backgroundColor: tokens.isDark ? 'rgba(0,0,0,0.45)' : 'rgba(15, 23, 42, 0.28)',
      }}
    >
      <Box
        onClick={onClose}
        sx={{ flex: 1, minHeight: 48, cursor: 'pointer' }}
        aria-label="Dismiss browse sheet"
      />
      <Box
        sx={{
          maxHeight: '88%',
          display: 'flex',
          flexDirection: 'column',
          borderTopLeftRadius: 18,
          borderTopRightRadius: 18,
          backgroundColor: tokens.isDark ? '#0B1220' : '#FFFFFF',
          border: `1px solid ${tokens.border}`,
          borderBottom: 0,
          boxShadow: '0 -12px 32px rgba(15, 23, 42, 0.18)',
          overflow: 'hidden',
          animation: 'browseSheetUp 0.22s ease-out',
          '@keyframes browseSheetUp': {
            from: { transform: 'translateY(18px)', opacity: 0.72 },
            to: { transform: 'translateY(0)', opacity: 1 },
          },
        }}
      >
        <Stack alignItems="center" sx={{ pt: 1, pb: 0.5 }}>
          <Box
            sx={{
              width: 36,
              height: 4,
              borderRadius: 999,
              backgroundColor: tokens.isDark ? 'rgba(148,163,184,0.35)' : '#D1D5DB',
            }}
          />
        </Stack>

        <Box sx={{ px: 2, pb: 1.25, display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
            <Typography
              sx={{
                fontSize: 10.5,
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: tokens.primaryAccent,
                mb: 0.45,
              }}
            >
              Help center
            </Typography>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: tokens.fontSize + 5,
                color: tokens.primary,
                letterSpacing: '-0.02em',
                lineHeight: 1.2,
              }}
            >
              Browse questions
            </Typography>
          </Box>
          <IconAction tokens={tokens} onClick={onClose} title="Close" filled>
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconAction>
        </Box>

        <Box sx={{ px: 2, pb: 1.25 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              height: 40,
              px: 1.35,
              borderRadius: 999,
              border: `1px solid ${tokens.border}`,
              backgroundColor: tokens.isDark ? '#111827' : '#F8FAFC',
            }}
          >
            <SearchIcon sx={{ fontSize: 18, color: tokens.muted }} />
            <InputBase
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search questions"
              sx={{
                flex: 1,
                fontSize: tokens.fontSize,
                color: tokens.primary,
                fontFamily: 'inherit',
                '& input::placeholder': { color: tokens.secondary, opacity: 1 },
              }}
            />
          </Box>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: 0.5, ...hideScrollbarSx }}>
          {filtered.map((question) => {
            const isActive = hovered === question;
            return (
              <Box
                key={question}
                onClick={() => onSelectQuestion(question)}
                onMouseEnter={() => setHovered(question)}
                onMouseLeave={() => setHovered(null)}
                sx={{
                  mx: 1.5,
                  px: 0.5,
                  py: 1.35,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  cursor: 'pointer',
                  borderBottom: `1px solid ${tokens.border}`,
                  textAlign: 'left',
                }}
              >
                <Typography
                  sx={{
                    flex: 1,
                    fontSize: tokens.fontSize,
                    fontWeight: isActive ? 600 : 500,
                    color: isActive ? tokens.primaryAccent : tokens.primary,
                    lineHeight: 1.35,
                  }}
                >
                  {question}
                </Typography>
                <ChevronRightIcon
                  sx={{
                    fontSize: 18,
                    color: isActive ? tokens.primaryAccent : tokens.muted,
                    flexShrink: 0,
                  }}
                />
              </Box>
            );
          })}
        </Box>

        <Typography
          sx={{
            px: 2,
            pt: 1.1,
            pb: 0.35,
            fontSize: 11.5,
            color: tokens.muted,
            textAlign: 'left',
          }}
        >
          {filtered.length} answer{filtered.length === 1 ? '' : 's'} available
        </Typography>

        <ComposerFooter
          tokens={tokens}
          placeholder="Ask a support question..."
          value={draft}
          onChange={onDraftChange}
          onSend={onSend}
        />
      </Box>
    </Box>
  );
};

const HistoryView: React.FC<{
  tokens: ThemeTokens;
  onOpenSession: (title: string) => void;
  onNewConversation: () => void;
}> = ({ tokens, onOpenSession, onNewConversation }) => (
  <Box
    sx={{
      flex: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      background: tokens.isDark
        ? 'linear-gradient(180deg, #0B1F3A 0%, #000000 42%)'
        : 'linear-gradient(180deg, #EEF5FF 0%, #FFFFFF 42%)',
      textAlign: 'left',
    }}
  >
    <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
      <Typography
        sx={{
          fontSize: 10.5,
          fontWeight: 700,
          letterSpacing: '0.1em',
          textTransform: 'uppercase',
          color: tokens.primaryAccent,
          mb: 0.55,
        }}
      >
        Your history
      </Typography>
      <Typography
        sx={{
          fontWeight: 800,
          fontSize: tokens.fontSize + 6,
          color: tokens.primary,
          letterSpacing: '-0.02em',
          lineHeight: 1.2,
          mb: 0.45,
        }}
      >
        Past conversations
      </Typography>
      <Typography sx={{ fontSize: tokens.fontSize - 0.5, color: tokens.secondary }}>
        Pick up where you left off.
      </Typography>
    </Box>

    <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', px: 2, pb: 1.5, ...hideScrollbarSx }}>
      <Stack spacing={1}>
        {HISTORY_SESSIONS.map((session) => (
          <Box
            key={session.id}
            onClick={() => onOpenSession(session.title)}
            sx={{
              p: 1.25,
              borderRadius: 2.25,
              border: `1px solid ${tokens.border}`,
              backgroundColor: tokens.isDark ? '#111827' : '#FFFFFF',
              display: 'flex',
              alignItems: 'center',
              gap: 1.1,
              cursor: 'pointer',
              '&:hover': {
                borderColor: withAlpha(tokens.primaryAccent, '55'),
              },
            }}
          >
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: 1.75,
                backgroundColor: withAlpha(tokens.primaryAccent, tokens.isDark ? '28' : '18'),
                color: tokens.primaryAccent,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <ChatBubbleOutlineIcon sx={{ fontSize: 17 }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                noWrap
                sx={{
                  fontWeight: 700,
                  fontSize: tokens.fontSize - 0.5,
                  color: tokens.primary,
                  lineHeight: 1.3,
                }}
              >
                {session.title}
              </Typography>
              <Typography
                noWrap
                sx={{ fontSize: tokens.fontSize - 2, color: tokens.secondary, lineHeight: 1.35 }}
              >
                {session.snippet}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 11, color: tokens.muted, flexShrink: 0 }}>
              {session.meta}
            </Typography>
            <ChevronRightIcon sx={{ fontSize: 18, color: tokens.muted, flexShrink: 0 }} />
          </Box>
        ))}
      </Stack>
    </Box>

    <Box sx={{ px: 2, pt: 0.75, pb: 1.5, flexShrink: 0 }}>
      <Box
        component="button"
        type="button"
        onClick={onNewConversation}
        sx={{
          width: '100%',
          height: 44,
          borderRadius: 2.25,
          border: `1px solid ${tokens.border}`,
          backgroundColor: tokens.isDark ? '#111827' : '#FFFFFF',
          color: tokens.primaryAccent,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.75,
          cursor: 'pointer',
          fontFamily: 'inherit',
          fontWeight: 700,
          fontSize: tokens.fontSize,
          '&:hover': {
            borderColor: withAlpha(tokens.primaryAccent, '66'),
            backgroundColor: withAlpha(tokens.primaryAccent, tokens.isDark ? '18' : '0D'),
          },
        }}
      >
        <AddIcon sx={{ fontSize: 18 }} />
        New conversation
      </Box>
      <Stack direction="row" spacing={0.6} alignItems="center" justifyContent="center" sx={{ mt: 1.1 }}>
        <LockOutlinedIcon sx={{ fontSize: 12, color: tokens.muted }} />
        <Typography sx={{ fontSize: 10.5, color: tokens.muted }}>
          Example and local prototype sessions
        </Typography>
      </Stack>
    </Box>
  </Box>
);

const PlaceholderView: React.FC<{
  tokens: ThemeTokens;
  title: string;
  subtitle: string;
  onBack: () => void;
}> = ({ tokens, title, subtitle, onBack }) => (
  <Box
    sx={{
      flex: 1,
      minHeight: 0,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      px: 3,
      textAlign: 'center',
      backgroundColor: tokens.panel,
    }}
  >
    <Typography sx={{ fontWeight: 700, fontSize: tokens.fontSize + 2, color: tokens.primary, mb: 0.75 }}>
      {title}
    </Typography>
    <Typography sx={{ fontSize: tokens.fontSize - 0.5, color: tokens.secondary, mb: 2 }}>{subtitle}</Typography>
    <Box
      component="button"
      onClick={onBack}
      sx={{
        border: `1px solid ${tokens.border}`,
        backgroundColor: tokens.surface,
        color: tokens.brand,
        borderRadius: 2,
        px: 1.5,
        py: 0.75,
        cursor: 'pointer',
        fontWeight: 600,
        fontSize: tokens.fontSize - 1,
        fontFamily: 'inherit',
      }}
    >
      Back to welcome
    </Box>
  </Box>
);

const ComposerFooter: React.FC<{
  tokens: ThemeTokens;
  placeholder: string;
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
}> = ({ tokens, placeholder, value, onChange, onSend }) => {
  const canSend = value.trim().length > 0;

  return (
    <Box
      sx={{
        px: 1.5,
        pt: 1.15,
        pb: 1.25,
        borderTop: `1px solid ${tokens.border}`,
        backgroundColor: tokens.surface,
        flexShrink: 0,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.85 }}>
        <InputBase
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              onSend();
            }
          }}
          placeholder={placeholder}
          sx={{
            flex: 1,
            height: 40,
            px: 1.4,
            borderRadius: 2.5,
            border: `1px solid ${tokens.border}`,
            backgroundColor: tokens.isDark ? '#111827' : '#F1F5F9',
            color: tokens.primary,
            fontSize: tokens.fontSize,
            fontFamily: 'inherit',
            '& input': {
              py: 0,
              '&::placeholder': {
                color: tokens.secondary,
                opacity: 1,
              },
            },
          }}
        />
        <Box
          component="button"
          type="button"
          onClick={onSend}
          disabled={!canSend}
          aria-label="Send message"
          sx={{
            width: 40,
            height: 40,
            border: 0,
            borderRadius: 2,
            backgroundColor: tokens.brand,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            boxShadow: `0 8px 14px ${tokens.brand}44`,
            cursor: canSend ? 'pointer' : 'default',
            opacity: canSend ? 1 : 0.55,
            p: 0,
          }}
        >
          <SendIcon sx={{ fontSize: 16 }} />
        </Box>
      </Stack>
      <Stack direction="row" spacing={0.6} alignItems="center" justifyContent="center">
        <LockOutlinedIcon sx={{ fontSize: 12, color: tokens.muted }} />
        <Typography sx={{ fontSize: 10.5, color: tokens.muted }}>
          Local design preview · No live assistant
        </Typography>
      </Stack>
    </Box>
  );
};

export default ChatBotPreview;
